import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ADMIN_PLANS,
  planLabel,
  type AdminOrgDetail,
  type AdminOrgSummary,
  type Paginated,
} from "@currencycore/shared";
import { getOrg, listOrgs } from "../lib/api.ts";
import { FilterBar, type FilterDef, type SortDef } from "../components/FilterBar.tsx";
import { Pagination } from "../components/Pagination.tsx";
import { Drawer } from "../components/Drawer.tsx";
import { RowMenu } from "../components/RowMenu.tsx";
import { PlanEditor } from "../components/PlanEditor.tsx";
import { MembersList } from "../components/MembersList.tsx";
import { PlanBadge, StatusBadge, Meter } from "../components/Badges.tsx";
import { Sparkline } from "../components/Charts.tsx";
import { toast } from "../components/Toast.tsx";
import { useDebounced, fmtDate, errMsg, filterParams, dashboardOrgUrl } from "../lib/ui.ts";

const PAGE_SIZE = 20;

const FILTER_DEFS: FilterDef[] = [
  { key: "plan", label: "Plan", options: ADMIN_PLANS.map((p) => ({ value: p, label: planLabel(p) })) },
  {
    key: "status",
    label: "Status",
    options: [
      { value: "active", label: "Active" },
      { value: "past_due", label: "Past due" },
      { value: "canceled", label: "Canceled" },
    ],
  },
];

const SORT_DEFS: SortDef[] = [
  { value: "createdAt", label: "Created" },
  { value: "name", label: "Name" },
  { value: "plan", label: "Plan" },
  { value: "usage", label: "Usage" },
  { value: "members", label: "Members" },
];

export function OrganizationsPage() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paginated<AdminOrgSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const debSearch = useDebounced(search);
  const navigate = useNavigate();

  useEffect(() => setPage(1), [debSearch, filters, sort, order]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listOrgs({ q: debSearch, page, pageSize: PAGE_SIZE, sort, order, ...filterParams(filters) })
      .then((res) => alive && setData(res))
      .catch((e) => alive && toast(errMsg(e, "Failed to load organizations.")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [debSearch, filters, sort, order, page]);

  function refresh() {
    listOrgs({ q: debSearch, page, pageSize: PAGE_SIZE, sort, order, ...filterParams(filters) })
      .then(setData)
      .catch(() => {});
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Organizations</h1>
          <p className="muted">{data ? `${data.total.toLocaleString("en-US")} total` : "Loading…"}</p>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearch={setSearch}
        defs={FILTER_DEFS}
        filters={filters}
        onFilters={setFilters}
        sortDefs={SORT_DEFS}
        sort={sort}
        order={order}
        onSort={(s, o) => {
          setSort(s);
          setOrder(o);
        }}
        placeholder="Search by name, slug, or ID…"
      />

      <div className="card table-card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Plan</th>
                <th className="col-num">Members</th>
                <th>Usage (period)</th>
                <th>Status</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={7} className="table-loading"><span className="spinner" /> Loading…</td></tr>
              ) : data && data.items.length === 0 ? (
                <tr><td colSpan={7} className="table-empty">No organizations match your filters.</td></tr>
              ) : (
                data?.items.map((o) => (
                  <tr key={o.id} className="clickable-row" onClick={() => setSelected(o.id)}>
                    <td>
                      <div className="cell-title">{o.name}</div>
                      <div className="cell-sub mono muted">{o.slug}</div>
                    </td>
                    <td><PlanBadge plan={o.plan} label={o.planLabel} /></td>
                    <td className="col-num tnum">{o.memberCount}</td>
                    <td style={{ minWidth: 160 }}><Meter used={o.currentUsage} limit={o.monthlyLimit} /></td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="muted tnum">{fmtDate(o.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        items={[
                          { label: "Quick view", onClick: () => setSelected(o.id) },
                          { label: "Open page", onClick: () => navigate(`/organizations/${o.id}`) },
                          { label: "Copy ID", onClick: () => navigator.clipboard?.writeText(o.id) },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && (
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
        )}
      </div>

      <OrgDrawer
        orgId={selected}
        onClose={() => setSelected(null)}
        onMutated={refresh}
      />
    </>
  );
}

function OrgDrawer({
  orgId,
  onClose,
  onMutated,
}: {
  orgId: string | null;
  onClose: () => void;
  onMutated: () => void;
}) {
  const [detail, setDetail] = useState<AdminOrgDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setDetail(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getOrg(orgId)
      .then((d) => alive && setDetail(d))
      .catch((e) => alive && toast(errMsg(e, "Failed to load organization.")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [orgId]);

  function update(d: AdminOrgDetail) {
    setDetail(d);
    onMutated();
  }

  return (
    <Drawer
      open={!!orgId}
      onClose={onClose}
      title={detail?.name ?? (loading ? "Loading…" : "Organization")}
      subtitle={detail ? <span className="mono">{detail.id}</span> : undefined}
      footer={
        detail ? (
          <>
            <a href={dashboardOrgUrl(detail.id)} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
              Dashboard ↗
            </a>
            <Link to={`/organizations/${detail.id}`} className="btn btn-ghost btn-sm" onClick={onClose}>
              Open full page →
            </Link>
          </>
        ) : undefined
      }
    >
      {!detail ? (
        <div className="drawer-loading"><span className="spinner" /> Loading…</div>
      ) : (
        <>
          <section className="drawer-section">
            <div className="section-row between">
              <h4>Last 30 days</h4>
              <span className="muted tnum">{detail.currentUsage.toLocaleString("en-US")} this period</span>
            </div>
            <Sparkline values={detail.recentUsage.map((p) => p.requests)} height={48} />
          </section>
          <PlanEditor detail={detail} onUpdated={update} />
          <MembersList orgId={detail.id} members={detail.members} onUpdated={update} />
        </>
      )}
    </Drawer>
  );
}
